import React, { useEffect, useState } from 'react';
import { Typography, message, Empty, Spin } from 'antd';
import { TournamentService } from '@/services/tournament.service';
import { Tournament, Match } from '@/types/api.types';
import { logger } from '@/utils/logger';
import { sortTournamentCategories } from '@/utils/tournamentCategorySort';
import {
  ResultsContainer,
  CategorySection,
  CategoryTitle,
  PlaceItem,
  MedalIcon,
  InitialsBox,
  ParticipantInfo,
  ParticipantName,
  ParticipantDetails
} from './styles/TournamentResults.styles';

const { Title } = Typography;

interface ParticipantResult {
  id: number;
  participantId: number;
  participantName: string;
  firstName: string;
  lastName: string;
  club?: string;
  city?: string;
  region?: string;
  categoryId: number;
  place: number;
}

interface CategoryResults {
  categoryId: number;
  categoryName: string;
  gender: 'male' | 'female' | 'mixed';
  ageFrom: number;
  ageTo: number;
  weightFrom: number;
  weightTo: number;
  results: ParticipantResult[];
}

interface TournamentResultsProps {
  tournamentId: number;
  tournament: Tournament;
}

export const TournamentResults: React.FC<TournamentResultsProps> = ({
  tournamentId,
}) => {
  const [categoryResults, setCategoryResults] = useState<CategoryResults[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Загружаем категории
      const categoriesData = await TournamentService.getTournamentCategories(tournamentId);
      
      // Загружаем матчи для расчета результатов
      const matches = await TournamentService.getTournamentMatches(tournamentId);
      const matchesArray = Array.isArray(matches) ? matches : (matches as any)?.data || [];

      // Группируем матчи по категориям
      const matchesByCategory = new Map<number, Match[]>();
      matchesArray.forEach((match: Match) => {
        if (match.categoryId) {
          if (!matchesByCategory.has(match.categoryId)) {
            matchesByCategory.set(match.categoryId, []);
          }
          matchesByCategory.get(match.categoryId)!.push(match);
        }
      });

      // Для каждой категории определяем места участников
      const resultsByCategory: CategoryResults[] = [];

      for (const category of categoriesData) {
        const categoryMatches = matchesByCategory.get(category.id) || [];
        
        // Находим финальный матч (последний раунд)
        const maxRound = Math.max(...categoryMatches.map(m => m.round || 0), 0);
        const finalMatches = categoryMatches.filter(m => m.round === maxRound);
        
        // Находим матч за 3 место
        // Сначала ищем матч, который является consolationMatchNumber для полуфиналов
        const semiFinalMatches = categoryMatches.filter(m => m.round === maxRound - 1);
        let thirdPlaceMatch: Match | undefined = undefined;
        
        if (semiFinalMatches.length > 0) {
          // Находим consolationMatchNumber из полуфиналов
          const consolationMatchNumber = semiFinalMatches[0]?.consolationMatchNumber;
          if (consolationMatchNumber) {
            thirdPlaceMatch = categoryMatches.find(m => m.matchNumber === consolationMatchNumber);
          }
        }
        
        // Если не нашли через consolationMatchNumber, ищем матч в последнем раунде, который не является финалом
        if (!thirdPlaceMatch && maxRound > 1) {
          const lastRoundMatches = categoryMatches.filter(m => m.round === maxRound);
          if (lastRoundMatches.length > finalMatches.length) {
            // Есть матч в последнем раунде, который не является финалом - это матч за 3 место
            thirdPlaceMatch = lastRoundMatches.find(m => !finalMatches.includes(m));
          }
        }

        const places = new Map<number, number>(); // participantId -> place
        
        // Определяем 1 и 2 место из финала
        for (const finalMatch of finalMatches) {
          if (finalMatch.status === 'completed' && finalMatch.winnerParticipantId) {
            const winnerId = finalMatch.winnerParticipantId;
            const loserId = finalMatch.participant1Id === winnerId 
              ? finalMatch.participant2Id 
              : finalMatch.participant1Id;
            
            places.set(winnerId, 1);
            if (loserId) {
              places.set(loserId, 2);
            }
          }
        }

        // Определяем 3 место из матча за 3 место
        if (thirdPlaceMatch && thirdPlaceMatch.status === 'completed' && thirdPlaceMatch.winnerParticipantId) {
          places.set(thirdPlaceMatch.winnerParticipantId, 3);
          
          // Проигравший в матче за 3 место получает 4 место
          const thirdPlaceLoserId = thirdPlaceMatch.participant1Id === thirdPlaceMatch.winnerParticipantId
            ? thirdPlaceMatch.participant2Id
            : thirdPlaceMatch.participant1Id;
          if (thirdPlaceLoserId) {
            places.set(thirdPlaceLoserId, 4);
          }
        } else if (maxRound > 1) {
          // Если нет отдельного матча за 3 место, оба проигравших в полуфинале получают 3 место
          for (const semiMatch of semiFinalMatches) {
            if (semiMatch.status === 'completed' && semiMatch.winnerParticipantId) {
              const loserId = semiMatch.participant1Id === semiMatch.winnerParticipantId
                ? semiMatch.participant2Id
                : semiMatch.participant1Id;
              if (loserId && !places.has(loserId)) {
                places.set(loserId, 3);
              }
            }
          }
        }

        // Собираем информацию об участниках с местами
        const participantResults: ParticipantResult[] = [];
        
        for (const [participantId, place] of places.entries()) {
          // Находим информацию об участнике из матчей
          let participantInfo: any = null;
          for (const match of categoryMatches) {
            const p1Id = match.participant1?.id || (match.participant1 as any)?.participantId || match.participant1Id;
            const p2Id = match.participant2?.id || (match.participant2 as any)?.participantId || match.participant2Id;
            
            if (p1Id === participantId) {
              participantInfo = match.participant1;
              break;
            }
            if (p2Id === participantId) {
              participantInfo = match.participant2;
              break;
            }
          }

          if (participantInfo) {
            const firstName = participantInfo.firstName || participantInfo.first_name || '';
            const lastName = participantInfo.lastName || participantInfo.last_name || '';
            const club = (participantInfo.club as any)?.name || participantInfo.clubName || (participantInfo.clubParticipants as any)?.[0]?.club?.name || '';
            const city = participantInfo.city || (participantInfo.club as any)?.city || '';
            const region = (participantInfo.federation as any)?.region || (participantInfo.club as any)?.region || (participantInfo.club as any)?.federation?.region || '';

            participantResults.push({
              id: participantId,
              participantId: participantId,
              participantName: `${lastName} ${firstName}`,
              firstName,
              lastName,
              club,
              city,
              region,
              categoryId: category.id,
              place,
            });
          }
        }

        // Сортируем по местам
        participantResults.sort((a, b) => a.place - b.place);

        if (participantResults.length > 0) {
          resultsByCategory.push({
            categoryId: category.id,
            categoryName: category.name,
            gender: (category.gender as 'male' | 'female' | 'mixed') || 'mixed',
            ageFrom: category.ageFrom || category.minAge || 0,
            ageTo: category.ageTo || category.maxAge || 100,
            weightFrom: category.weightFrom || 0,
            weightTo: category.weightTo || 200,
            results: participantResults,
          });
        }
      }

      // Сортировка: возраст → вес → девочки перед мальчиками
      setCategoryResults(sortTournamentCategories(resultsByCategory));
    } catch (error) {
      logger.error('Error fetching results:', error);
      message.error('Ошибка при загрузке результатов');
      setCategoryResults([]);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (firstName: string, lastName: string): string => {
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : '';
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${lastInitial}${firstInitial}`;
  };

  const formatCategoryName = (category: CategoryResults): string => {
    const genderText = category.gender === 'male' ? 'Мальчики' : category.gender === 'female' ? 'Девочки' : 'Смешанная';
    const ageText = category.ageFrom === category.ageTo 
      ? `${category.ageFrom} лет`
      : `${category.ageFrom}-${category.ageTo} лет`;
    
    let weightText = '';
    if (category.weightFrom === 0 && category.weightTo >= 200) {
      weightText = '';
    } else if (category.weightFrom === 0) {
      weightText = `, -${category.weightTo} кг`;
    } else if (category.weightTo >= 200) {
      weightText = `, ${category.weightFrom}+ кг`;
    } else {
      weightText = `, ${category.weightFrom}-${category.weightTo} кг`;
    }
    
    return `${genderText} ${ageText}${weightText}`;
  };

  const formatParticipantDetails = (result: ParticipantResult): string => {
    const parts: string[] = [];
    if (result.region) parts.push(result.region);
    if (result.city) parts.push(result.city);
    if (result.club) parts.push(result.club);
    return parts.join(', ');
  };

  if (loading) {
    return (
      <ResultsContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </ResultsContainer>
    );
  }

  if (categoryResults.length === 0) {
    return (
      <ResultsContainer>
        <Title level={3}>Индивидуальные результаты</Title>
        <Empty
          description="Результаты пока не доступны"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </ResultsContainer>
    );
  }

  return (
    <ResultsContainer>
      <Title level={3} style={{ marginBottom: 24 }}>Индивидуальные результаты</Title>
      
      {categoryResults.map((category) => (
        <CategorySection key={category.categoryId}>
          <CategoryTitle>{formatCategoryName(category)}</CategoryTitle>
          
          {category.results.map((result) => (
            <PlaceItem key={result.id}>
              <MedalIcon $place={result.place}>
                {result.place === 1 && '🥇'}
                {result.place === 2 && '🥈'}
                {result.place === 3 && '🥉'}
                {result.place > 3 && `${result.place}.`}
              </MedalIcon>
              
              <InitialsBox>
                {getInitials(result.firstName, result.lastName)}
              </InitialsBox>
              
              <ParticipantInfo>
                <ParticipantName>{result.participantName}</ParticipantName>
                <ParticipantDetails>{formatParticipantDetails(result)}</ParticipantDetails>
              </ParticipantInfo>
            </PlaceItem>
          ))}
        </CategorySection>
      ))}
    </ResultsContainer>
  );
};

export default TournamentResults;
